import { isCopilotPlanningTool } from "@/features/copilot/lib/copilot-tool-labels";

/// How each part of one assistant message is shown.
/// - answer: normal text (and any part type this plan does not manage)
/// - interim: narration written before a later tool call ("Let me check..."), shown muted
/// - card: a read in the final batch before the answer; it may draw a data card
/// - row: an earlier read that only fed a later step; one quiet line, never a card
/// - action: a write waiting for or past the person's decision; always its confirmation card
/// - hidden: planning steps, failed calls the model retried, and repeats of the row above
export type CopilotPartRole = "answer" | "interim" | "card" | "row" | "action" | "hidden";

interface PlanPart {
  type: string;
  text?: string;
  name?: string;
  toolName?: string;
  toolCallId?: string;
  args?: unknown;
  result?: unknown;
  isError?: boolean;
}

function capabilityOf(part: PlanPart): string {
  const name: unknown =
    typeof part.args === "object" && part.args !== null ? Reflect.get(part.args, "name") : null;
  return typeof name === "string" ? name : (part.toolName ?? "");
}

function failed(part: PlanPart): boolean {
  if (part.isError) return true;
  return typeof part.result === "object" && part.result !== null
    ? Reflect.get(part.result, "ok") === false
    : false;
}

function hasText(part: PlanPart): boolean {
  return part.type === "text" && (part.text ?? "").trim() !== "";
}

export function planCopilotMessageParts(
  parts: readonly PlanPart[],
  isAction: (toolCallId: string) => boolean = () => false,
): CopilotPartRole[] {
  const lastTool = parts.findLastIndex((part) => part.type === "tool-call");
  const lastNarration = parts.findLastIndex((part, index) => index < lastTool && hasText(part));
  const roles: CopilotPartRole[] = [];
  let previousRow: string | null = null;

  parts.forEach((part, index) => {
    // The card data is drawn by the tool chip it belongs to, never as a part of its own.
    if (part.type === "data" && part.name === "copilot-read") {
      roles.push("hidden");
      return;
    }
    if (part.type !== "tool-call") {
      roles.push(part.type === "text" && index < lastTool ? "interim" : "answer");
      if (hasText(part)) previousRow = null;
      return;
    }
    if (part.toolCallId && isAction(part.toolCallId)) {
      roles.push("action");
      previousRow = null;
      return;
    }
    if (isCopilotPlanningTool(part.toolName ?? "") || failed(part)) {
      roles.push("hidden");
      return;
    }
    const capability = capabilityOf(part);
    if (index > lastNarration) {
      roles.push("card");
      previousRow = null;
      return;
    }
    roles.push(previousRow === capability ? "hidden" : "row");
    previousRow = capability;
  });
  return roles;
}
